// ──────────────────────────────────────────────────────────────
// src/pages/cliente/ClienteHistorialPage.tsx
// Historial de clases del cliente logueado
// ──────────────────────────────────────────────────────────────
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useReservas } from "../../hooks/useReservas";
import { useAuth } from "../../context/AuthContext";
import ReservaDialog from "../../components/ReservaDialog";
import type { ReservaEvt } from "../../api/ReservaEvt";

// Extiende la fila con el flag opcional (puede no venir en algunas vistas)
type HistRow = ReservaEvt & { firmadoCliente?: boolean };

export default function ClienteHistorialPage() {
  const { t } = useTranslation<"translation">();
  const { user } = useAuth();

  const [mes, setMes] = useState(dayjs()); // mes en curso
  const [sel, setSel] = useState<HistRow | null>(null); // reserva seleccionada

  const desde = mes.startOf("month").format("YYYY-MM-DD");
  const hasta = mes.endOf("month").format("YYYY-MM-DD");

  const { rows, loading } = useReservas(desde, hasta, {
    clienteIdUsr: user?.id,
  });

  const histRows = rows as HistRow[];

  if (loading) return <CircularProgress />;

  return (
    <>
      {/* Navegación meses */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={() => setMes((m) => m.subtract(1, "month"))}>
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, textAlign: "center", textTransform: "capitalize" }}
        >
          {mes.format("MMMM YYYY")}
        </Typography>
        <IconButton onClick={() => setMes((m) => m.add(1, "month"))}>
          <ArrowForward />
        </IconButton>
      </Stack>

      {/* Tabla simple */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("clienteHistorial.tabla.fecha")}</TableCell>
            <TableCell>{t("clienteHistorial.tabla.hora")}</TableCell>
            <TableCell>{t("clienteHistorial.tabla.profesor")}</TableCell>
            <TableCell>{t("clienteHistorial.tabla.asignatura")}</TableCell>
            <TableCell>{t("clienteHistorial.tabla.estadoFirmas")}</TableCell>
            <TableCell align="right">{t("clienteHistorial.tabla.acciones")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {histRows.map((r) => {
            const hIni = (r.horaInicio ?? "").slice(0, 5);
            const hFin = (r.horaFin ?? "").slice(0, 5);
            return (
              <TableRow key={r.id} hover>
                <TableCell>{dayjs(r.fecha).format("DD/MM/YYYY")}</TableCell>
                <TableCell>{hIni && hFin ? `${hIni}-${hFin}` : "—"}</TableCell>
                <TableCell>{r.nombreProf}</TableCell>
                <TableCell>{r.nombreAsignatura}</TableCell>
                <TableCell>
                  {r.estado}
                  {/* Marca si el propio alumno/cliente ya firmó (si el back lo envía) */}
                  {r.firmadoCliente && " · ✔️"}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setSel(r)}>
                    {t("clienteHistorial.verFirmar")}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {histRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t("clienteHistorial.sinResultados") ?? "No hay clases en este periodo."}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Diálogo reutilizado */}
      <ReservaDialog
        open={Boolean(sel)}
        reserva={sel ?? undefined}
        onClose={() => setSel(null)}
        onEdit={() => {}} // los clientes no editan desde aquí
      />
    </>
  );
}
